import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PlusIcon, XIcon } from 'lucide-react';
import {
  Button,
  FormField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@noor/ui';
import { cn } from '@noor/lib';
import {
  toModifierSelection,
  useModifierGroups,
  type MenuModifierGroup,
} from '@/entities/menu';
import { ModifierGroupCard } from './modifier-group-card';

/**
 * A blank group, matching the design's default state.
 *
 * `single` is the default because it is the safer of the two: it means the
 * customer MUST pick exactly one, so a half-configured group cannot silently
 * become an optional free-for-all on the storefront.
 *
 * The seeded option starts with BLANK fiscal classifiers: a modifier option
 * is its own fiscal line item, so copying the product's IKPU/unit into it
 * would silently misclassify the option at fiscalization time. Both fields
 * are required by the schema, so the merchant picks them explicitly.
 */
function buildEmptyGroup() {
  return {
    nameRu: '',
    nameUz: '',
    nameEn: '',
    choiceType: 'single' as const,
    required: true,
    options: [
      { nameRu: '', nameUz: '', nameEn: '', priceDelta: 0, ikpuCode: '', unitCode: '' },
    ],
  };
}

/** A group's chip text: its Ru name, or a positional stand-in while unnamed. */
function chipLabel(name: string | undefined, fallback: string): string {
  const trimmed = name?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : fallback;
}

function toFormGroup(group: MenuModifierGroup) {
  return {
    ...(group.id === undefined ? {} : { id: group.id }),
    nameRu: group.name.ru,
    nameUz: group.name.uz ?? '',
    nameEn: group.name.en ?? '',
    choiceType: toModifierSelection(group.maxSelect),
    required: group.minSelect >= 1,
    minSelect: group.minSelect,
    ...(group.maxSelect !== undefined && group.maxSelect !== 1
      ? { maxSelect: group.maxSelect }
      : {}),
    options: group.options.map((option) => ({
      ...(option.id === undefined ? {} : { id: option.id }),
      nameRu: option.name.ru,
      nameUz: option.name.uz ?? '',
      nameEn: option.name.en ?? '',
      priceDelta: option.priceDelta,
      // Blank when the catalogue carries none — never the product's own
      // classifiers (see `buildEmptyGroup`): each option is its own fiscal
      // line item, and the schema requires both fields explicitly.
      ikpuCode: option.ikpuCode ?? '',
      unitCode: option.unitCode ?? '',
      ...(option.vatPercent === undefined ? {} : { vatPercent: option.vatPercent }),
    })),
  };
}

/**
 * Wizard step 3 (Figma 840-17545 / 840-17987): the product's modifier groups.
 *
 * Each group is a card; the chips above name every group at a glance and remove
 * one. A product may have no groups at all — most do — so the step opens empty
 * with a single call to action rather than a pre-added blank card.
 */
export function ModifierStep() {
  const { t } = useTranslation('eats');
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: 'modifiers' });
  const modifierGroups = useModifierGroups();

  // The chips mirror the Ru names as they are typed, so `useWatch` rather than
  // `fields` — `useFieldArray`'s snapshot does not update as inputs change.
  const groups = useWatch({ control, name: 'modifiers' }) as
    | { id?: string; nameRu?: string }[]
    | undefined;
  const modifiersDisabled = useWatch({ control, name: 'modifiersDisabled' }) === true;
  const selectedGroupIds = new Set(
    (groups ?? []).map((group) => group.id).filter((id): id is string => Boolean(id)),
  );
  const availableGroups =
    modifierGroups.data?.filter(
      (group): group is MenuModifierGroup & { id: string } =>
        group.id !== undefined && !selectedGroupIds.has(group.id),
    ) ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* The dish-wide off switch (`PUT /merchant/products/modifiers-disabled`).
       * Only meaningful once the dish has groups to switch off — but it stays
       * visible while ON, so a dish whose groups were all removed can still be
       * switched back. The groups stay editable either way: the backend keeps
       * them and brings them back as they are. */}
      {(fields.length > 0 || modifiersDisabled) && (
        <FormField name="modifiersDisabled" className="rounded-3xl bg-muted px-5 py-4">
          <FormField.Switch label={t('menu.product.form.modifiers.disabledToggle')} />
          <FormField.Description>
            {modifiersDisabled
              ? t('menu.product.form.modifiers.disabledHintOn')
              : t('menu.product.form.modifiers.disabledHintOff')}
          </FormField.Description>
        </FormField>
      )}

      {availableGroups.length > 0 && (
        <div className="flex flex-col gap-1.5 rounded-3xl bg-muted px-5 py-4">
          <span className="px-1 text-sm font-medium tracking-tight text-foreground">
            {t('menu.product.form.modifiers.existingTitle')}
          </span>
          <Select
            value=""
            onValueChange={(groupId) => {
              const group = availableGroups.find((candidate) => candidate.id === groupId);
              if (group !== undefined) append(toFormGroup(group));
            }}
          >
            <SelectTrigger
              aria-label={t('menu.product.form.modifiers.existingTitle')}
              className="w-full bg-card"
            >
              <SelectValue placeholder={t('menu.product.form.modifiers.existingTitle')} />
            </SelectTrigger>
            <SelectContent>
              {availableGroups.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name.ru || t('menu.product.form.modifiers.unnamedExisting')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {fields.length > 0 && (
        <ul className="flex flex-wrap items-center gap-2.5">
          {fields.map((field, index) => (
            <li key={field.id}>
              <span
                className={cn(
                  'flex h-8 items-center gap-1 rounded-full px-2.5 text-xs font-medium',
                  index === 0
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground',
                )}
              >
                {/* An unnamed group still needs a chip, or removing it becomes
                 * impossible — it falls back to its position. A BLANK name must
                 * fall through too, not just a missing one, so this cannot be
                 * `??`: a group whose name was cleared is exactly the case. */}
                {chipLabel(
                  groups?.[index]?.nameRu,
                  t('menu.product.form.modifiers.untitledGroup', { index: index + 1 }),
                )}
                <button
                  type="button"
                  onClick={() => remove(index)}
                  aria-label={t('menu.product.form.modifiers.removeGroup')}
                  className={cn(
                    'rounded-full p-0.5',
                    index === 0
                      ? 'hover:bg-primary-foreground/20'
                      : 'hover:bg-foreground/10',
                  )}
                >
                  <XIcon className="size-3.5" aria-hidden />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {fields.map((field, index) => (
        <ModifierGroupCard key={field.id} index={index} />
      ))}

      {fields.length === 0 && (
        <p className="rounded-3xl bg-muted p-6 text-center text-sm text-muted-foreground">
          {t('menu.product.form.modifiers.empty')}
        </p>
      )}

      <Button
        type="button"
        variant="ghost"
        onClick={() => append(buildEmptyGroup())}
        className="h-12 self-end rounded-full bg-card px-4 text-base font-semibold text-primary hover:bg-muted"
      >
        <PlusIcon className="size-[18px]" aria-hidden />
        {t('menu.product.form.modifiers.addGroup')}
      </Button>
    </div>
  );
}
